/* eslint-disable max-lines */
/* eslint-disable max-statements */

/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  HomeVideo,
  HomeVideoAddInput,
  HomeVideoUpdateInput,
  LabelsSearchItem,
  RegenerateScreenshotMetadataFieldInput,
  StoreLinkUpdateInput,
  StoresSearchItem
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { Props as ImageSelectorAndCarouselProps } from "components/ImageSelectorAndCarousel"
import extractFQDN from "helpers/urls/extractFQDN"
import useItemView, { UseItemViewReturn } from "hooks/useListItemView"
import {
  ChangeEvent,
  createRef,
  Dispatch,
  FormEventHandler,
  RefCallback,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"

import Item from "./Item"

export interface DeleteGuardrailDependency {
  id: string
  title: string
  type: string
}

const IMDB_ID_REGEX = /^tt\d{7,8}$/iu
const IMDB_ID_FORMAT_ERROR = "IMDb ID must match tt####### or tt########"

interface StoreLinkInput {
  currencyCode?: string | null
  currencySymbol?: string | null
  id?: string
  isInStock?: boolean | null
  isPreOrder?: boolean
  priceAmount?: number | null
  priceFormatted?: string | null
  storeId?: string
  store?: {
    id: string
    url?: string | null
  }
  url?: string
  localKey?: string
}

interface StoreLinkInputRefs {
  isPreOrder: RefObject<HTMLInputElement | null>
  storeId: RefObject<HTMLSelectElement | null>
  url: RefObject<HTMLInputElement | null>
}

type BulkToggleMode = "preorder" | "buy" | "mixed"

const createLocalStoreLinkKey = (() => {
  let counter = 0
  return () => {
    counter += 1
    return `storeLink-${Date.now()}-${counter}`
  }
})()

const withLocalKey = (link: StoreLinkInput): StoreLinkInput => ({
  ...link,
  localKey: link.localKey ?? link.id ?? createLocalStoreLinkKey()
})

const deriveBulkToggleMode = (links: StoreLinkInput[]): BulkToggleMode => {
  if (!links.length) {
    return "mixed"
  }

  const preorderCount = links.filter((link) => Boolean(link.isPreOrder)).length

  if (preorderCount === 0) {
    return "buy"
  }

  if (preorderCount === links.length) {
    return "preorder"
  }

  return "mixed"
}

interface UseBulkToggleParams {
  setStoreLinks: Dispatch<SetStateAction<StoreLinkInput[]>>
  storeLinkRefs: StoreLinkInputRefs[]
  storeLinks: StoreLinkInput[]
}

const useBulkStoreLinksToggle = ({
  setStoreLinks,
  storeLinkRefs,
  storeLinks
}: UseBulkToggleParams) => {
  const [bulkToggleMode, setBulkToggleMode] = useState<BulkToggleMode>(() =>
    deriveBulkToggleMode(storeLinks)
  )

  useEffect(() => {
    setBulkToggleMode(deriveBulkToggleMode(storeLinks))
  }, [storeLinks])

  const setCheckboxRefsToValue = useCallback(
    (isPreOrder: boolean) => {
      storeLinkRefs.forEach((storeLinkRef) => {
        if (storeLinkRef.isPreOrder.current) {
          storeLinkRef.isPreOrder.current.checked = isPreOrder
        }
      })
    },
    [storeLinkRefs]
  )

  const onBulkToggleChange = useCallback(
    (isPreOrder: boolean) => {
      setStoreLinks((prev) =>
        prev.map((link) => ({
          ...link,
          isPreOrder
        }))
      )
      setBulkToggleMode(isPreOrder ? "preorder" : "buy")
      setCheckboxRefsToValue(isPreOrder)
    },
    [setCheckboxRefsToValue, setStoreLinks]
  )

  const appendStoreLink = useCallback(() => {
    setStoreLinks((prev) => [
      ...prev,
      {
        isPreOrder: bulkToggleMode === "preorder",
        localKey: createLocalStoreLinkKey()
      }
    ])
  }, [bulkToggleMode, setStoreLinks])

  return {
    appendStoreLink,
    bulkToggleChecked: bulkToggleMode === "preorder",
    bulkToggleDisabled: storeLinks.length === 0,
    bulkToggleMode,
    onBulkToggleChange
  }
}

export interface Props {
  deleteGuardrailDependencies?: DeleteGuardrailDependency[]
  deleteGuardrailMessage?: string
  isAdd?: boolean
  isAdmin?: boolean
  isEditing?: boolean
  isEditor?: boolean
  isLabelsSuggestionsLoading?: boolean
  isDeleteLoading?: boolean
  isSaveLoading?: boolean
  isStoresSuggestionsLoading?: boolean
  item?: HomeVideo
  labels: LabelsSearchItem[]
  onCancel: () => void
  onDelete?: () => void
  onDeleteGuardrailDismiss?: () => void
  onEdit?: () => void

  onGetLabelsSuggestions: (input: { inputValue: string }) => void

  onGetStoresSuggestions: (input: { inputValue: string }) => void
  onInferScreenshotCredit?: (url: string) => Promise<string | undefined>
  onRegenerateScreenshotMetadataField?: (
    input: RegenerateScreenshotMetadataFieldInput
  ) => Promise<string | undefined>
  onPublishToggle?: () => void
  onSave: (
    input: HomeVideoUpdateInput,

    storeLinks?: StoreLinkUpdateInput[]
  ) => void

  onSelectLabelId: (id: string, name: string) => void

  onSelectStoreId: (index: number) => (id: string, name: string) => void
  selectedLabelId?: string
  selectedStoreIds?: { id: string; index: number }[]
  shouldCloseDeleteModalOnConfirm?: boolean
  storeOptionsById: Record<string, StoresSearchItem>
  stores: StoresSearchItem[]
}

export interface ComponentProps
  extends
    Omit<
      Props,
      | "isEditing"
      | "isSaveLoading"
      | "onCancel"
      | "onDelete"
      | "onEdit"
      | "onPublishToggle"
      | "onSave"
    >,
    Omit<UseItemViewReturn, "item" | "refs"> {
  bulkToggleChecked?: boolean
  bulkToggleDisabled?: boolean
  editionsRef: RefCallback<HTMLInputElement>
  isActionButtonLoading?: boolean
  isUHDRef: RefCallback<HTMLInputElement>
  imdbIdRef: RefCallback<HTMLInputElement>
  imdbIdError?: string | null
  nameRef: RefCallback<HTMLInputElement>
  onAddStoreLink: () => void
  onBulkToggleChange?: (value: boolean) => void

  onDeleteStoreLink: (index: number) => () => void

  onSetReleaseDate: (releaseDate: Date | string) => void
  onInferScreenshotCredit?: (url: string) => Promise<string | undefined>
  onRegenerateScreenshotMetadataField?: (
    input: RegenerateScreenshotMetadataFieldInput
  ) => Promise<string | undefined>
  onSetSelectedImages: ImageSelectorAndCarouselProps["onSetSelectedImages"]
  onSetSelectedScreenshots: ImageSelectorAndCarouselProps["onSetSelectedImages"]
  onStoreUrlChange: (
    index: number
  ) => (event: ChangeEvent<HTMLInputElement>) => void
  onSubmit: FormEventHandler
  onResetImdbIdError: () => void
  releaseDate?: string
  selectedImages: ImageSelectorAndCarouselProps["selectedImages"]
  selectedScreenshots: ImageSelectorAndCarouselProps["selectedImages"]
  storeLinkRefs: StoreLinkInputRefs[]
  storeLinks: StoreLinkInput[]
  storeOptionsById: Record<string, StoresSearchItem>
}

const usePrepareComponent = ({
  item,
  isEditing,
  isSaveLoading,
  onCancel,
  onDelete,
  onEdit,
  onGetStoresSuggestions,
  onPublishToggle,
  onSave,
  onSelectStoreId,
  selectedStoreIds,
  storeOptionsById,
  stores,
  ...props
}: Props): ComponentProps => {
  const [selectedImages, setSelectedImages] = useState<
    ImageSelectorAndCarouselProps["selectedImages"]
  >(
    () =>
      item?.images?.map((image) => ({
        contentId: image.id ? Number(image.id) : undefined,
        filename: image.filename ?? image.url,
        originalUrl: image.url
      })) ?? []
  )
  const [selectedScreenshots, setSelectedScreenshots] = useState<
    ImageSelectorAndCarouselProps["selectedImages"]
  >(
    () =>
      item?.screenshots?.map((screenshot) => ({
        alternativeText: screenshot.alternativeText ?? undefined,
        caption: screenshot.caption ?? undefined,
        contentId: screenshot.id ? Number(screenshot.id) : undefined,
        credit: screenshot.credit ?? undefined,
        filename: screenshot.filename ?? screenshot.url,
        id: screenshot.id,
        originalUrl: screenshot.url
      })) ?? []
  )
  const [imdbIdError, setImdbIdError] = useState<string | null>(null)

  const [releaseDate, setReleaseDate] = useState<string | undefined>(
    () => item?.releaseDate
  )

  const {
    actionButtonLabel,
    actionButtonOnClick,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick
  } = useItemView<HomeVideo>({
    fields: [],
    isAdd: props.isAdd,
    isEditing,
    isSaveLoading,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle,
    shouldCloseModalOnDeleteConfirm: props.shouldCloseDeleteModalOnConfirm
  })

  const [storeLinks, setStoreLinks] = useState<StoreLinkInput[]>(
    item?.storeLinks
      ? item.storeLinks.map((link) => withLocalKey({ ...link }))
      : []
  )
  const [storeLinkRefs, setStoreLinkRefs] = useState<StoreLinkInputRefs[]>([])
  const pendingStoreSelectionIndexRef = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false

    const nextStoreLinks = item?.storeLinks
      ? item.storeLinks.map((link) => withLocalKey({ ...link }))
      : []

    queueMicrotask(() => {
      if (!cancelled) {
        setStoreLinks(nextStoreLinks)
      }
    })

    return () => {
      cancelled = true
    }
  }, [item])

  useEffect(() => {
    let cancelled = false

    if (storeLinkRefs.length !== storeLinks.length) {
      const nextStoreLinkRefs = storeLinks.map(
        (__, index) =>
          storeLinkRefs[index] || {
            isPreOrder: createRef<HTMLInputElement>(),
            storeId: createRef<HTMLSelectElement>(),
            url: createRef<HTMLInputElement>()
          }
      )

      queueMicrotask(() => {
        if (!cancelled) {
          setStoreLinkRefs(nextStoreLinkRefs)
        }
      })
    }

    return () => {
      cancelled = true
    }
  }, [storeLinkRefs, storeLinks])

  /**
   * Input focus on newly added and empty store link
   */
  useEffect(() => {
    const latestStoreLinkRef = storeLinkRefs[storeLinks.length - 1]

    /**
     * If new store link url is empty focus
     */
    if (
      latestStoreLinkRef?.url.current &&
      !latestStoreLinkRef.url.current.value
    ) {
      latestStoreLinkRef.url.current?.focus()
    }
  }, [storeLinkRefs, storeLinks.length])

  const {
    bulkToggleChecked,
    bulkToggleDisabled,
    onBulkToggleChange,
    appendStoreLink
  } = useBulkStoreLinksToggle({
    setStoreLinks,
    storeLinkRefs,
    storeLinks
  })

  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const editionsInputRef = useRef<HTMLInputElement | null>(null)
  const isUHDInputRef = useRef<HTMLInputElement | null>(null)
  const imdbIdInputRef = useRef<HTMLInputElement | null>(null)

  const nameRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    nameInputRef.current = node
  }, [])

  const editionsRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    editionsInputRef.current = node
  }, [])

  const isUHDRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    isUHDInputRef.current = node
  }, [])

  const imdbIdRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    imdbIdInputRef.current = node
  }, [])
  const handleResetImdbIdError = () => setImdbIdError(null)

  const onAddStoreLink: ComponentProps["onAddStoreLink"] = () => {
    appendStoreLink()
  }

  const onDeleteStoreLink: ComponentProps["onDeleteStoreLink"] =
    (index) => () => {
      setStoreLinks((prev) =>
        prev.filter((___, storeLinkIndex) => storeLinkIndex !== index)
      )
    }

  const onSubmit: FormEventHandler = (event) => {
    event.preventDefault()

    if (!nameInputRef.current?.value) {
      return
    }

    const editionsValue = editionsInputRef.current?.value?.trim()

    let imdbId: string | undefined

    if (props.isAdd) {
      const imdbIdInputValue = imdbIdInputRef.current?.value?.trim() ?? ""
      const normalizedImdbId = imdbIdInputValue
        ? imdbIdInputValue.toLowerCase()
        : ""

      if (normalizedImdbId && !IMDB_ID_REGEX.test(normalizedImdbId)) {
        setImdbIdError(IMDB_ID_FORMAT_ERROR)
        return
      }

      setImdbIdError(null)
      imdbId = normalizedImdbId || undefined
    } else {
      setImdbIdError(null)
    }

    const editions = editionsValue
      ? editionsValue
          .split(",")
          .map((edition) => edition.trim())
          .filter(Boolean)
      : null

    const updatedStoreLinks = storeLinks
      .map((link, index) => ({
        id: link.id,
        isPreOrder: Boolean(storeLinkRefs[index].isPreOrder.current?.checked),
        isPublished: item?.isPublished,
        storeId: selectedStoreIds?.find((id) => id.index === index)?.id,
        url: storeLinkRefs[index].url.current?.value ?? link.url
      }))
      .filter(
        // All fields are required
        (link) =>
          link.storeId &&
          link.url &&
          link.storeId.trim() !== "" &&
          link.url.trim() !== ""
      )

    const homeVideoInput: HomeVideoAddInput | HomeVideoUpdateInput = {
      editions,
      images: selectedImages.map((image) => ({
        contentId: image.contentId ? image.contentId : undefined,
        originalUrl: image.originalUrl
      })),
      screenshots: selectedScreenshots.map((screenshot) => ({
        alternativeText: screenshot.alternativeText?.trim() || undefined,
        caption: screenshot.caption?.trim() || undefined,
        credit: screenshot.credit?.trim() || undefined,
        contentId: screenshot.contentId ? screenshot.contentId : undefined,
        originalUrl: screenshot.originalUrl
      })),
      isUHD: Boolean(isUHDInputRef.current?.checked),
      labelId: props.selectedLabelId,
      name: nameInputRef.current.value,
      releaseDate: releaseDate ?? undefined
    }

    const getIsAddInput = (
      homeVideoInputToCheck: HomeVideoAddInput | HomeVideoUpdateInput
    ): homeVideoInputToCheck is HomeVideoAddInput => Boolean(props.isAdd)

    if (
      props.isAdd &&
      typeof imdbId !== "undefined" &&
      getIsAddInput(homeVideoInput)
    ) {
      homeVideoInput.imdbId = imdbId
    }

    onSave(homeVideoInput, updatedStoreLinks)
  }

  const onSetSelectedImages: ImageSelectorAndCarouselProps["onSetSelectedImages"] =
    (newSelectedImages) => {
      setSelectedImages(newSelectedImages)
    }
  const onSetSelectedScreenshots: ImageSelectorAndCarouselProps["onSetSelectedImages"] =
    (newSelectedScreenshots) => {
      setSelectedScreenshots(newSelectedScreenshots)
    }

  /**
   * onRegenerateScreenshotMetadataField
   *
   * @description
   * Regenerates a single screenshot metadata field using the current Home
   * Video context (IMDb id, title, and release date) as fallback payload.
   * @param {RegenerateScreenshotMetadataFieldInput} input - Field regenerate
   * payload for the selected screenshot.
   * @returns {Promise<string | undefined>} Regenerated field value, or
   * undefined when regeneration is unavailable.
   *
   */
  const onRegenerateScreenshotMetadataField: ComponentProps["onRegenerateScreenshotMetadataField"] =
    useCallback(
      async (input: RegenerateScreenshotMetadataFieldInput) => {
        if (!props.onRegenerateScreenshotMetadataField) {
          return undefined
        }

        const currentImdbId = imdbIdInputRef.current?.value?.trim()
        const fallbackImdbId = item?.mas?.imdbId?.trim()
        const title = nameInputRef.current?.value?.trim() || item?.name
        const normalizedTitle = title?.trim()
        const normalizedReleaseDate = (releaseDate ?? item?.releaseDate)?.trim()

        return await props.onRegenerateScreenshotMetadataField({
          ...input,
          ...(currentImdbId || fallbackImdbId
            ? { imdbId: currentImdbId || fallbackImdbId }
            : {}),
          ...(normalizedReleaseDate
            ? { releaseDate: normalizedReleaseDate }
            : {}),
          ...(normalizedTitle ? { title: normalizedTitle } : {})
        })
      },
      [
        item?.mas?.imdbId,
        item?.name,
        item?.releaseDate,
        props.onRegenerateScreenshotMetadataField,
        releaseDate
      ]
    )

  /**
   * onSetReleaseDate
   *
   * @description
   * Stores the selected Home Video release date for form submission and
   * metadata regeneration fallback context.
   * @param {Date | string} date - Release date selected in the form.
   * @returns {void} Updates the local release date state.
   *
   */
  const onSetReleaseDate: ComponentProps["onSetReleaseDate"] = (date) => {
    setReleaseDate(date as string)
  }

  /**
   * onStoreUrlChange
   *
   * @description
   * Handles the change event for the store URL input field and preloads store suggestions.
   * @param {number} index - Store link index being edited.
   * @returns {(event: ChangeEvent<HTMLInputElement>) => void} handler that reacts to URL input changes.
   *
   */
  const onStoreUrlChange: ComponentProps["onStoreUrlChange"] =
    (index) => (event) => {
      const host = extractFQDN(event.currentTarget.value)

      if (host) {
        onGetStoresSuggestions({ inputValue: host })
        const autosuggestInput = document.getElementById(
          `search-storeLink-${index}`
        ) as HTMLInputElement | null
        autosuggestInput?.focus()
        autosuggestInput?.click()
        pendingStoreSelectionIndexRef.current = index
      }
    }

  /**
   * blurStoreAutosuggest
   *
   * @description
   * Removes focus from the autosuggest input at the provided index to hide suggestion overlays.
   * @param {number} index - Store link index whose autosuggest input should lose focus.
   * @returns {void}
   *
   */
  const blurStoreAutosuggest = useCallback((index: number) => {
    const autosuggestInput = document.getElementById(
      `search-storeLink-${index}`
    ) as HTMLInputElement | null

    autosuggestInput?.blur()
  }, [])

  useEffect(() => {
    const pendingStoreSelectionIndex = pendingStoreSelectionIndexRef.current
    if (pendingStoreSelectionIndex === null) return

    const alreadySelected = selectedStoreIds?.some(
      (selected) => selected.index === pendingStoreSelectionIndex
    )

    if (alreadySelected) {
      pendingStoreSelectionIndexRef.current = null
      return
    }

    if (stores.length === 1) {
      const [store] = stores

      if (!store?.id) {
        pendingStoreSelectionIndexRef.current = null
        return
      }

      const storeName = store.name ?? store.id

      onSelectStoreId(pendingStoreSelectionIndex)(store.id, storeName)
      blurStoreAutosuggest(pendingStoreSelectionIndex)
      pendingStoreSelectionIndexRef.current = null

      return
    }

    if (stores.length > 1) {
      pendingStoreSelectionIndexRef.current = null
    }
  }, [blurStoreAutosuggest, onSelectStoreId, selectedStoreIds, stores])

  return {
    ...props,
    bulkToggleChecked,
    bulkToggleDisabled,
    actionButtonLabel,
    actionButtonOnClick,
    editionsRef,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    isUHDRef,
    imdbIdError,
    imdbIdRef,
    item,
    modalContent,
    nameRef,
    onAddStoreLink,
    onBulkToggleChange,
    onDeleteStoreLink,
    onGetStoresSuggestions,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    onSelectStoreId,
    onRegenerateScreenshotMetadataField,
    onSetReleaseDate,
    onSetSelectedImages,
    onSetSelectedScreenshots,
    onResetImdbIdError: handleResetImdbIdError,
    onStoreUrlChange,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    releaseDate,
    selectedImages,
    selectedScreenshots,
    selectedStoreIds,
    storeLinkRefs,
    storeLinks,
    storeOptionsById,
    stores
  }
}

export { usePrepareComponent }

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
